import Vue from 'vue'

/**
 * Membuat instance Vue untuk menangani pengambilan data secara paginasi (bertahap).
 *
 * Fungsi ini mengembalikan objek Vue yang memiliki state dan method `fetchData`
 * untuk melakukan pemanggilan data menggunakan fungsi `fetchFn`.
 *
 * @param {(params: { offset: number, limit: number, [key: string]: any }) => Promise<{ data: any[] }>} fetchFn
 *   Fungsi async yang digunakan untuk mengambil data, menerima `offset`, `limit`, dan parameter opsional lainnya.
 * @param {number} [defaultOffset=0] - Offset awal untuk paginasi.
 * @param {number} [defaultLimit=10] - Jumlah data per halaman yang diambil.
 * @param {(currentOffset: number, itemsLength: number) => number} [offsetCallback] - Callback untuk mengatur bagaimana offset di-update.
 *   Default: (offset, itemsLength) => offset + itemsLength
 *
 * @returns {Vue} Objek Vue dengan data reaktif dan method `fetchData` untuk mengambil data secara paginasi.
 *
 * @example
 * const fetcher = createPaginatedFetcher(api.getUsers)
 * fetcher.fetchData()
 *
 * @example
 * // Reset data dan ambil ulang dari awal
 * fetcher.fetchData({ reset: true })
 *
 * @example
 * // Ambil data dengan opsi tambahan
 * fetcher.fetchData({ options: { search: 'abc' } })
 *
 * @example
 * // Custom offset callback - increment by 1
 * const fetcher = createPaginatedFetcher(api.getUsers, 0, 50, (offset, itemsLength) => offset + 1)
 *
 * @example
 * // Custom offset callback - based on items length
 * const fetcher = createPaginatedFetcher(api.getUsers, 0, 50, (offset, itemsLength) => offset + itemsLength)
 */
// eslint-disable-next-line import/prefer-default-export
export function createPaginatedFetcher(fetchFn, defaultOffset = 0, defaultLimit = 50, offsetCallback = null) {
  const defaultOffsetCallback = (currentOffset, itemsLength) => currentOffset + itemsLength

  return new Vue({
    data() {
      return {
        data: [],
        offset: defaultOffset,
        limit: defaultLimit,
        isLoading: false,
        error: null,
        hasMore: false,
        options: {},
        offsetCallback: offsetCallback || defaultOffsetCallback,
        requestId: 0,
      }
    },
    methods: {
      async fetchData({ reset = false, options = {} } = {}) {
        if (!reset && (this.isLoading || (!this.hasMore && this.offset > 0))) return

        if (reset) {
          this.offset = defaultOffset
          this.data = []
          this.hasMore = false
          this.options = options
        }

        this.requestId += 1
        const currentRequestId = this.requestId
        this.isLoading = true
        this.error = null

        try {
          const result = await fetchFn({ offset: this.offset, limit: this.limit, ...this.options })
          if (currentRequestId !== this.requestId) return

          const items = result?.data || []

          this.data = reset ? [...items] : [...this.data, ...items]
          // Use callback to update offset
          this.offset = this.offsetCallback(this.offset, items.length)
          this.hasMore = items.length >= this.limit
        } catch (err) {
          if (currentRequestId !== this.requestId) return
          this.error = err
        } finally {
          if (currentRequestId === this.requestId) {
            this.isLoading = false
          }
        }
      },
    },
  })
}
